import { ChevronLeft, LogOut } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { type ReactNode } from 'react';
import { Box, Flex, IconButton } from '@/components/common';
import { authService } from '@/services';

interface WizardHeaderProps {
  left?: ReactNode;
  centerContent?: ReactNode;
  right?: ReactNode;
  /** Convenience: renders back button in left when left is omitted. */
  onBack?: () => void;
  /** Renders a logout button in right when right is omitted. */
  withLogoutButton?: boolean;
  withBackButton?: boolean;
  /** Use soft IconButtons over photo/video backgrounds (ghost is invisible there). */
  hasMedia?: boolean;
  /**
   * `center` (default) — balanced three-slot; centerContent stays middle.
   * `start` — centerContent sits immediately after the back/left control.
   */
  align?: 'start' | 'center';
}

/** Header chrome — back / center / right. Default layout keeps center visually centered. */
export function WizardHeader({
  left,
  centerContent,
  right,
  onBack,
  withLogoutButton,
  withBackButton,
  hasMedia = false,
  align = 'center',
}: WizardHeaderProps) {
  const { t } = useTranslation('common');
  const iconVariant = hasMedia ? 'soft' : 'ghost';

  const leftSlot =
    left ??
    (withBackButton !== false && onBack ? (
      <IconButton
        type="button"
        variant={iconVariant}
        size="3"
        onClick={onBack}
        aria-label={t('actions.back')}
      >
        <ChevronLeft size={20} strokeWidth={2.5} aria-hidden />
      </IconButton>
    ) : null);

  const rightSlot =
    right ??
    (withLogoutButton ? (
      <IconButton
        type="button"
        variant={iconVariant}
        color="gray"
        size="2"
        onClick={() => void authService.signOut()}
        aria-label={t('navigation.signOut')}
      >
        <LogOut size={20} strokeWidth={2.5} aria-hidden />
      </IconButton>
    ) : null);

  if (align === 'start') {
    return (
      <Flex align="center" gap="2" width="100%" className="px-5 pb-2">
        <Flex align="center" gap="2" minWidth="0" flexGrow="1">
          {leftSlot}
          {centerContent}
        </Flex>
        {rightSlot}
      </Flex>
    );
  }

  return (
    <Flex align="center" gap="2" width="100%" px="3" pb="1" className="pt-safe-area">
      <Flex flexGrow="1" flexBasis="0" minWidth="0" justify="start" align="center">
        {leftSlot}
      </Flex>

      {centerContent && (
        <Box flexShrink="0" px="1">
          {centerContent}
        </Box>
      )}

      <Flex flexGrow="1" flexBasis="0" minWidth="0" justify="end" align="center">
        {rightSlot}
      </Flex>
    </Flex>
  );
}
